import React from 'react';
import { Box, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { gray60, gray80, green50, red50 } from '../../../constants/colors';
import { regularSm } from '../../../constants/location';
import {
  emailMessageStatus,
  EmailMessageStatus,
  emailRejectReason,
  EmailRejectReason,
} from '../../../constants/enum';
import { EmailMessage } from '../../../types/emailImport';

// ステータスの表示（OCR一覧の読取ステータスに合わせ「アイコン＋色テキスト」で表現）
type StatusTone = 'neutral' | 'success' | 'error';

const statusMeta: Record<EmailMessageStatus, { label: string; tone: StatusTone }> = {
  [emailMessageStatus.received]: { label: '受信', tone: 'neutral' },
  [emailMessageStatus.processing]: { label: '処理中', tone: 'neutral' },
  [emailMessageStatus.completed]: { label: '完了', tone: 'success' },
  [emailMessageStatus.rejected]: { label: '対象外', tone: 'error' },
  [emailMessageStatus.failed]: { label: '失敗', tone: 'error' },
  [emailMessageStatus.bounced]: { label: 'バウンス', tone: 'error' },
};

const statusToneColor: Record<StatusTone, string> = {
  neutral: gray60,
  success: green50,
  error: red50,
};

// rejected の内訳を利用者向けの日本語で表示する
export const emailRejectReasonLabel: Record<EmailRejectReason, string> = {
  [emailRejectReason.notAllowlisted]: '許可されていない送信元',
  [emailRejectReason.authFailed]: '送信元認証（SPF/DKIM/DMARC）に失敗',
  [emailRejectReason.unsupportedExtension]: '非対応の添付形式',
  [emailRejectReason.tooLarge]: '添付サイズが上限を超過',
  [emailRejectReason.tooManyAttachments]: '添付数が上限を超過',
  [emailRejectReason.tooManyPages]: '合計ページ数が上限を超過',
  [emailRejectReason.pdfTooManyPages]: 'PDFのページ数が上限を超過',
  [emailRejectReason.noAttachment]: '添付ファイルなし',
  [emailRejectReason.inboxDisabled]: '受信アドレスが無効',
  [emailRejectReason.inboxNotFound]: '宛先アドレスが未登録',
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap' as const,
  },
  icon: {
    fontSize: 20,
  },
  reason: {
    ...regularSm,
    color: gray80,
    fontSize: '12px',
    marginLeft: '4px',
  },
};

interface Props {
  message: EmailMessage;
  /** rejected 理由を括弧書きで併記するか（詳細モーダル向け。既定 true） */
  showReason?: boolean;
  /** rejected 理由をホバーのツールチップで表示するか（一覧向け。ラベルは簡潔に「対象外」のみ） */
  reasonTooltip?: boolean;
}

/**
 * 受信メールのステータス表示（アイコン＋色テキスト）。一覧・詳細モーダルで共通利用する。
 * 一覧では reasonTooltip でホバー表示、詳細では showReason でインライン併記する。
 */
const EmailStatusLabel: React.FC<Props> = ({
  message,
  showReason = true,
  reasonTooltip = false,
}) => {
  const meta = statusMeta[message.status];
  const icon =
    meta.tone === 'success' ? (
      <CheckCircleOutlineIcon sx={styles.icon} />
    ) : meta.tone === 'error' ? (
      <CancelOutlinedIcon sx={styles.icon} />
    ) : (
      <RotateRightIcon sx={styles.icon} />
    );

  // rejected の理由ラベル（対象外以外は空）
  const reasonText =
    message.status === emailMessageStatus.rejected && message.reject_reason
      ? emailRejectReasonLabel[message.reject_reason]
      : '';

  const content = (
    <Box sx={[styles.container, { color: statusToneColor[meta.tone] }]}>
      {icon}
      <span>{meta.label}</span>
      {showReason && reasonText && (
        <Box component="span" sx={styles.reason}>
          （{reasonText}）
        </Box>
      )}
    </Box>
  );

  // 一覧はラベルを簡潔にしつつ、ホバーで理由が分かるようにする
  if (reasonTooltip && reasonText) {
    return <Tooltip title={reasonText}>{content}</Tooltip>;
  }
  return content;
};

export default EmailStatusLabel;
